import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import SITE from '@/config/site'

const OG_LOCALE: Record<string, string> = {
  'pt-BR': 'pt_BR',
  'en-US': 'en_US',
  'es-ES': 'es_ES',
}

interface PageMetaProps {
  title: string
  description: string
  keywords?: string
  image?: string
  url?: string
  noindex?: boolean
}

export function PageMeta({
  title,
  description,
  keywords,
  image = SITE.url(SITE.seo.defaultImage),
  url,
  noindex = false,
}: PageMetaProps) {
  const { i18n } = useTranslation()
  const location = useLocation()
  const fullTitle = `${title} | ${SITE.seo.titleSuffix}`
  const robots = noindex ? 'noindex, nofollow' : SITE.seo.robots
  const ogLocale = OG_LOCALE[i18n.language] ?? 'pt_BR'
  const canonicalUrl = SITE.baseUrl + location.pathname

  return (
    <Helmet>
      <title>{fullTitle}</title>

      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={SITE.organization.name} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={SITE.seo.googlebot} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content={SITE.seo.type} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:site_name" content={SITE.organization.name} />
      {url && <meta property="og:url" content={url} />}

      <meta name="twitter:card" content={SITE.seo.twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <link rel="canonical" href={canonicalUrl} />
    </Helmet>
  )
}
